import { $command, $ctx, $inputRule, $node, $remark } from '@milkdown/kit/utils'
import { InputRule } from '@milkdown/kit/prose/inputrules'

import { TextTag } from '@campai/lib-common/utils/misc'

const textTagRegex = /\[\[([^\]]+)\]\]/g

type AllowedTextTagsContext = {
    allowedTags: TextTag[]
    allowedTagsText: string[]
}

export const allowedTextTagsCtx = $ctx<AllowedTextTagsContext, 'rich-text-editor-allowed-tags'>(
    {
        allowedTags: [],
        allowedTagsText: [],
    },
    'rich-text-editor-allowed-tags'
)

export const textTagPlugin = () => {
    const remarkTextTag = $remark('remarkTextTag', () => () => (tree) => {
        const visit = (node: any) => {
            if (node.type === 'text' && typeof node.value === 'string') {
                const value = node.value
                let match
                let lastIndex = 0
                const nodes = []

                while ((match = textTagRegex.exec(value)) !== null) {
                    if (match.index > lastIndex) {
                        nodes.push({
                            type: 'text',
                            value: value.slice(lastIndex, match.index),
                        })
                    }

                    nodes.push({ type: 'textTag', content: match[1] })
                    lastIndex = textTagRegex.lastIndex
                }

                if (lastIndex < value.length) {
                    nodes.push({ type: 'text', value: value.slice(lastIndex) })
                }

                return nodes.length > 0 ? nodes : [node]
            }

            if (Array.isArray(node.children)) {
                node.children = node.children.flatMap(visit)
            }

            return [node]
        }

        tree.children = tree.children.flatMap(visit)
        return tree
    })

    const textTagNode = $node('textTag', (ctx) => ({
        group: 'inline',
        inline: true,
        atom: true,
        attrs: { content: { default: '' }, tick: { default: 0 } },
        leafText: (node) => node.attrs.content,
        toolbarSelectable: true,
        parseDOM: [
            {
                tag: 'span.milkdown-tag-highlight',
                getAttrs: (dom) => ({
                    content: (dom as HTMLElement).dataset.tag ?? '',
                }),
            },
        ],
        toDOM: (node) => {
            const content = String(node.attrs.content ?? '')
            const normalizedContent = content.toLowerCase()
            const context = ctx.get(allowedTextTagsCtx.key)
            const isValid =
                context.allowedTagsText.length > 0 ? context.allowedTagsText.includes(normalizedContent) : true
            const tagObject = context.allowedTags.find((tag) => tag.tag === normalizedContent)

            return [
                'span',
                {
                    class: `milkdown-tag-highlight ${!isValid ? 'milkdown-tag-highlight-error' : ''}`,
                    'data-tag': content,
                },
                tagObject?.name || `[[${content}]]`,
            ]
        },
        parseMarkdown: {
            match: (node) => node.type === 'textTag',
            runner: (state, node, type) => {
                state.addNode(type, { content: node.content as string })
            },
        },
        toMarkdown: {
            match: (node) => node.type.name === 'textTag',
            runner: (state, node) => {
                state.addNode('text', undefined, undefined, {
                    value: `[[${node.attrs.content}]]`,
                })
            },
        },
    }))

    const textTagInputRule = $inputRule(
        (ctx) =>
            new InputRule(textTagRegex, (state, match, start, end) => {
                const [matched, content = ''] = match
                const { tr } = state

                if (matched) {
                    tr.replaceWith(start, end, textTagNode.type(ctx).create({ content }))
                }

                return tr
            })
    )

    const insertTextTagCommand = $command('InsertTag', (ctx) => {
        return (payload: string | undefined) => (state, dispatch) => {
            const content = payload || ''
            const { selection, tr } = state

            if (!selection) {
                return false
            }

            const node = textTagNode.type(ctx).create({ content })

            if (dispatch) {
                tr.insert(selection.to, node)
                dispatch(tr)
            }

            return true
        }
    })

    const refreshTextTagNodesCommand = $command('RefreshTagNodes', () => {
        return () => (state, dispatch) => {
            const { tr, doc } = state
            let changed = false

            doc.descendants((node, pos) => {
                if (node.type.name === 'textTag') {
                    changed = true
                    tr.setNodeMarkup(pos, node.type, {
                        ...node.attrs,
                        tick: (node.attrs.tick || 0) + 1,
                    })
                }
            })

            if (changed && dispatch) {
                tr.setMeta('addToHistory', false)
                dispatch(tr)
            }

            return true
        }
    })

    return [
        allowedTextTagsCtx,
        remarkTextTag,
        textTagInputRule,
        textTagNode,
        insertTextTagCommand,
        refreshTextTagNodesCommand,
    ].flat()
}
